import { useEffect, useRef } from 'react';
import * as React from 'react';
import { Transition } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';
import tw, { TwStyle } from 'twin.macro';
import 'styled-components/macro';

import { TextButton } from 'component-lib';
import { Icon } from 'component-lib/atoms/icon/Icon';
import { Card } from 'component-lib/molecules/card/Card';

export interface ModalProps {
  onClose?: (e: React.MouseEvent<HTMLElement> | KeyboardEvent) => void;
  title?: string;
  width: 'small' | 'medium' | 'large';
  children: React.ReactNode;
  open: boolean;
  hideCloseIcon?: boolean;
  className?: string;
}

const modalTransitionStyles: Partial<Record<TransitionStatus, TwStyle>> = {
  entering: tw`scale-100`,
  entered: tw`scale-100`,
};

export const Modal = ({
  children,
  onClose,
  open,
  width,
  title,
  hideCloseIcon = false,
  className,
  ...props
}: ModalProps) => {
  const childContainer = useRef(null);
  const container = useRef(null);
  const lastEventEl = useRef(null);

  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      e.code === 'Escape' && onClose?.(e);
    };
    document.addEventListener('keydown', onEscape);

    return () => {
      document.removeEventListener('keydown', onEscape);
    };
  }, [onClose]);

  return (
    <Transition appear in={open} timeout={100} mountOnEnter unmountOnExit {...props}>
      {transitionState => (
        <Backdrop
          ref={container}
          data-testid="backdrop"
          onMouseUp={e => {
            container.current === lastEventEl.current && onClose?.(e);
          }}
          onMouseDown={() => {
            lastEventEl.current = container.current;
          }}
          css={transitionState === 'entered' ? tw`bg-opacity-60` : tw`bg-opacity-0`}
        >
          <div
            tw="transition transform scale-50 duration-fast max-h-screen flex text-white"
            css={modalTransitionStyles[transitionState]}
            ref={childContainer}
            onMouseUp={e => {
              e.stopPropagation();
            }}
            onMouseDown={e => {
              e.stopPropagation();
              lastEventEl.current = childContainer.current;
            }}
          >
            <Card
              tw="flex flex-col overflow-hidden bg-[#3D4556] text-white rounded-none h-screen w-screen sm:(h-auto rounded-sm)"
              css={[
                [width === 'small' && tw`sm:max-w-modal-small`],
                [width === 'medium' && tw`sm:max-w-modal-medium`],
                [width === 'large' && tw`sm:max-w-modal-large`],
              ]}
              className={className}
              role="dialog"
            >
              {title && (
                <div tw="sm:rounded-t-sm border-b-1 border-white border-opacity-5 py-2 px-3" {...props}>
                  <div tw="flex justify-between items-center flex-1">
                    <h3 tw="font-light text-2xl" data-testid="modal-title">
                      {title}
                    </h3>

                    {!hideCloseIcon && (
                      <TextButton onClick={onClose} tw="text-gray-15 hover:text-white">
                        <Icon tw="text-3xl text-current" name="circle-remove" />
                      </TextButton>
                    )}
                  </div>
                </div>
              )}
              {children}
            </Card>
          </div>
        </Backdrop>
      )}
    </Transition>
  );
};

const Backdrop = tw.div`fixed top-0 left-0 w-full h-full bg-[#222] bg-opacity-20 flex items-center justify-center transition duration-fast z-overlay`;

export const ModalBody = tw.div`flex-1 py-2 px-3 overflow-y-auto`;

export const ModalControls = tw.div`flex justify-end px-3 py-2`;

export const ModalFooter = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div tw="border-t-1 border-white border-opacity-5" className={className}>
    <ModalControls>{children}</ModalControls>
  </div>
);
